import React, { useState } from 'react';
import { ShoppingCart, Plus, Star, Tag, X, ShoppingBag, Construction, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { shopProducts } from '../data/mock';
import { ShareButton } from './ShareButton';
import { NotifyMeButton } from './NotifyMeButton';
import { toast } from 'sonner';

export const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showUnderConstruction] = useState(true); // Under construction mode

  const categories = ['All', 'Bundles', 'Apparel', 'Books', 'Music', 'Comics'];

  const filteredProducts = selectedCategory === 'All'
    ? shopProducts
    : shopProducts.filter(product => product.category === selectedCategory);

  const addToCart = (product) => {
    if (showUnderConstruction) {
      toast.info('Store opening July 17, 2026! Stay tuned.');
      return;
    }
    setCart([...cart, product]);
    toast.success(`${product.name} added to cart!`);
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    toast.info('Item removed from cart');
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price, 0);
  const donationAmount = cartTotal * 0.10;

  return (
    <>
      {/* UNDER CONSTRUCTION BANNER */}
      {showUnderConstruction && (
        <div className="bg-gradient-to-r from-amber-500/20 via-amber-600/30 to-amber-500/20 border-y border-amber-500/50 py-4 px-6 mb-8">
          <div className="max-w-4xl mx-auto flex items-center justify-center space-x-4">
            <Construction className="text-amber-400 animate-pulse" size={28} />
            <div className="text-center">
              <p className="text-amber-200 font-bold text-lg">GITCHA SUM GOODIES - COMING SOON!</p>
              <p className="text-amber-300/80 text-sm flex items-center justify-center">
                <Calendar size={14} className="mr-1" />
                Official Store Launch: <span className="font-bold ml-1">July 17, 2026</span>
              </p>
            </div>
            <Construction className="text-amber-400 animate-pulse" size={28} />
          </div>
        </div>
      )}

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-gunmetal-950/95 backdrop-blur-sm">
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-gunmetal-900 shadow-2xl overflow-y-auto">
            <div className="p-6">
              {/* Cart Header */}
              <div className="flex items-center justify-between mb-6 border-b border-steel-700 pb-4">
                <h3 className="text-2xl font-black text-steel-100 flex items-center">
                  <ShoppingBag className="mr-2 text-accent-red" size={28} />
                  Your Cart
                </h3>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-steel-400 hover:text-accent-red transition-colors"
                >
                  <X size={28} />
                </button>
              </div>

              {/* Cart Items */}
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="text-steel-600 mx-auto mb-4" size={64} />
                  <p className="text-steel-400 text-lg">Your cart is empty</p>
                  <p className="text-steel-500 text-sm mt-2">Add some goodies!</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item, index) => (
                      <div key={`cart-${item.id}-${index}`} className="bg-gunmetal-800 rounded-lg p-4 flex items-center space-x-4">
                        <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded" />
                        <div className="flex-1">
                          <h4 className="text-steel-100 font-semibold text-sm">{item.name}</h4>
                          <p className="text-accent-red font-bold">${item.price}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="text-steel-500 hover:text-accent-red transition-colors"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Cart Summary */}
                  <div className="bg-gunmetal-950 rounded-lg p-6 space-y-3 mb-6">
                    <div className="flex justify-between text-steel-300">
                      <span>Subtotal:</span>
                      <span className="font-semibold">${cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-accent-red border-t border-steel-700 pt-3">
                      <span className="font-semibold">10% Donation:</span>
                      <span className="font-bold">${donationAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-steel-100 text-xl font-black border-t border-steel-700 pt-3">
                      <span>Total:</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-steel-500 italic text-center pt-2">
                      ${donationAmount.toFixed(2)} goes to our core causes
                    </p>
                  </div>

                  <Button
                    className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-bold py-4 text-lg"
                    onClick={() => toast.success('Checkout coming soon! Your purchase supports our causes.')}
                  >
                    Proceed to Checkout
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <section id="shop" className="relative py-24 bg-gunmetal-950">
        {/* Brick Wall Background */}
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://images.unsplash.com/photo-1759207027215-28106e5de290"
            alt="Brownstone Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-950/95 via-gunmetal-900/90 to-gunmetal-950"></div>
          <div className="absolute inset-0 opacity-40 noise-texture"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Cart */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex-1"></div>
            <button
              onClick={() => setShowCart(true)}
              className="relative bg-accent-red hover:bg-accent-red-dark text-white font-bold px-6 py-3 rounded-lg transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-accent-red/30"
            >
              <ShoppingCart size={24} />
              <span>Cart</span>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-accent-red w-6 h-6 rounded-full flex items-center justify-center text-sm font-black">
                  {cart.length}
                </span>
              )}
            </button>
          </div>

          {/* Section Header - Graffiti Style */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4 graffiti-text">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-red-500 to-yellow-400 drop-shadow-[0_0_15px_rgba(255,215,0,0.8)] tracking-wider transform -rotate-1 inline-block">
                GITCHA SUM
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 drop-shadow-[0_0_15px_rgba(255,0,0,0.8)] tracking-wider transform rotate-2 inline-block mt-2">
                GOODIES!
              </span>
            </h2>
            <div className="w-32 h-2 bg-accent-red mx-auto mb-6 transform -rotate-1"></div>
            <p className="text-xl md:text-2xl text-steel-300 max-w-3xl mx-auto font-bold mb-2">
              MINDESIGN Exclusive Gear, Paraphernalia & Merchandise
            </p>
            <p className="text-accent-red font-semibold text-lg">
              10% of Every Purchase Goes to Our Core Causes 🎯
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-bold transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-accent-red text-white shadow-lg shadow-accent-red/30 scale-105'
                    : 'bg-gunmetal-800 text-steel-300 hover:bg-gunmetal-700 border border-steel-700/50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className="group bg-gradient-to-br from-gunmetal-800/70 to-gunmetal-900/70 backdrop-blur-sm rounded-lg overflow-hidden border-2 border-steel-700/30 hover:border-accent-red transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Product Image */}
                <div className="relative h-80 overflow-hidden bg-gunmetal-950">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex flex-col space-y-2">
                    {product.featured && (
                      <span className="bg-accent-red px-3 py-1 rounded-full text-white text-xs font-bold flex items-center space-x-1">
                        <Star size={14} fill="white" />
                        <span>FEATURED</span>
                      </span>
                    )}
                    {product.bestseller && (
                      <span className="bg-yellow-500 px-3 py-1 rounded-full text-black text-xs font-bold">
                        BESTSELLER
                      </span>
                    )}
                    {product.limited && (
                      <span className="bg-purple-600 px-3 py-1 rounded-full text-white text-xs font-bold">
                        LIMITED
                      </span>
                    )}
                  </div>

                  {/* Discount Badge */}
                  {product.originalPrice && (
                    <div className="absolute top-4 right-4 bg-green-600 px-3 py-1 rounded-full text-white text-xs font-bold">
                      SAVE ${(product.originalPrice - product.price).toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-accent-red/20 text-accent-red rounded-full text-xs font-semibold mb-3">
                    {product.category}
                  </span>

                  <h3 className="text-xl font-bold text-steel-100 mb-2 group-hover:text-accent-red transition-colors duration-300 leading-tight">
                    {product.name}
                  </h3>

                  <p className="text-steel-400 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Price */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {product.originalPrice ? (
                        <div>
                          <span className="text-steel-500 line-through text-sm mr-2">
                            ${product.originalPrice}
                          </span>
                          <span className="text-accent-red font-black text-2xl">
                            ${product.price}
                          </span>
                        </div>
                      ) : (
                        <span className="text-accent-red font-black text-2xl">
                          ${product.price}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Add to Cart / Notify Me */}
                  {showUnderConstruction ? (
                    <NotifyMeButton
                      productId={product.id}
                      productName={product.name}
                    />
                  ) : (
                    <Button
                      onClick={() => addToCart(product)}
                      className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-bold py-3 transition-all duration-300 group/btn"
                    >
                      <Plus className="mr-2 group-hover/btn:scale-110 transition-transform" size={20} />
                      Add to Cart
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Banner */}
          <div className="mt-16 bg-gradient-to-r from-accent-red to-accent-red-dark rounded-lg p-8 text-center text-white">
            <h3 className="text-3xl font-black mb-4">Shop With Purpose. Change Lives.</h3>
            <p className="text-xl mb-2">
              Every purchase automatically donates <span className="font-black">10%</span> to our core causes.
            </p>
            <p className="text-white/80">
              Your goodies support childhood illnesses, cancer, mental health, homelessness, addiction recovery, and domestic violence advocacy.
            </p>
          </div>

          <div className="mt-8 text-center">
            <ShareButton
              sectionId="shop"
              sectionTitle="MINDESIGN Shop"
              description="Chokey & Hackenberry collections, THE REDACTED merch, and more — every purchase donates 10% to MINDESIGN's core causes."
            />
          </div>
        </div>
      </section>
    </>
  );
};
